// hooks, navigation, auth
import { useAuth } from "auth/Auth";
import { useNavigate } from "react-router-dom";
import React, {useState, useEffect} from "react";

// Material UI
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import Snackbar from "@mui/material/Snackbar";
import IconButton from "@mui/material/IconButton";
import PearlLogoLoader from "components/PearlLogoLoader";
import Grid from "@mui/material/Grid";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";

// Material UI icons
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

// My modules
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import HighSeasonSurchargeModal from "./HighSeasonSurchargeModal";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";
import { useMemo } from "react";
import StatusBadge from "components/StatusBadge";
import MonoText from "components/MonoText";


const HighSeasonSurcharge = () => {

    const page_title = 'HSS template'
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();

    // Initializing states

    const [isEditing, setIsEditing] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });

    // Trigger GET request on first render

    useEffect(() => {
        fetchTemplates();
    }, []);

    // GET request to fetch hss templates data.

    const fetchTemplates = async () => {
        setIsLoading(true);
        setIsCreating(false);
        try {
            const response = await axiosInstance.get('/hss');
            setTemplates(response.data);
        } catch (error) {
            console.error(`Error fetching ${page_title}:`, error);
            setSnackbar({ open: true, message: `Error loading ${page_title}`, severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle Save operation when the template is saved.
    // POST request for creating new entry.
    // PUT request for updating existing entry.

    const handleSaveTemplate = async (formData) => {
        try {
            const editing = !!selectedTemplate;
            setIsEditing(editing);
            setIsCreating(true);
            if (selectedTemplate) {
                await axiosInstance.put(`/hss/${selectedTemplate.id}`, formData);
                setSnackbar({ open: true, message: `${page_title} updated successfully`, severity: 'success' });
            } else {
                await axiosInstance.post('/hss', formData);
                setSnackbar({ open: true, message: `${page_title} created successfully`, severity: 'success' });
            }
            // Send GET request to get updated list of hss templates.
            fetchTemplates();
            handleCloseModal();
        } catch (error) {
            console.error('Error saving template:', error);
            setSnackbar({ open: true, message: 'Error saving template', severity: 'error' });
        } finally {
            setIsCreating(false);
            setIsEditing(false);
        }
    }

    // Modal close handler.

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedTemplate(null);
    }

    // Update delete state when delete button is clicked.

    const handleDeleteClick = (id) => {
        setDeleteConfirm({ open: true, id });
    };

    // Handle delete operation for the row.

    const handleDelete = async () => {
        try {
            await axiosInstance.delete(`/hss/${deleteConfirm.id}`);
            setSnackbar({ open: true, message: `${page_title} deleted successfully`, severity: 'success' });
            fetchTemplates();
        } catch (error) {
            console.error('Error deleting template:', error);
            setSnackbar({ open: true, message: 'Error deleting template', severity: 'error' });
            throw error;
        }
    }

    const handleCloseDeleteModal = () => setDeleteConfirm({ open: false, id: null });

    // Open detailed view page on row click

    const handleRowClick = (rowData) => {
        navigate(`/hss/${rowData.id}`, { state: { name: rowData.name } });
    }

    const tableData = useMemo(() => ({
        columns: [
            {
                Header: "Name",
                accessor: "template_name",
                width: "18%",
                Cell: ({ row }) => (
                    <MDTypography component="span" onClick={() => handleRowClick(row.original)} variant="caption" color="info" fontWeight="medium" sx={{ cursor: "pointer" }}>
                        {row.original.template_name}
                    </MDTypography>
                ),
            },
            { Header: "HSS %", accessor: "hss_percentage", width: "10%", align: "right", Cell: ({ value }) => <MonoText align="right">{value ? `${value}%` : "-"}</MonoText> },
            { Header: "Currency", accessor: "currency", width: "12%", Cell: ({ value }) => (value ? value.toUpperCase() : "-") },
            { Header: "Valid From", accessor: "valid_from", width: "14%" },
            { Header: "Valid Until", accessor: "valid_until", width: "14%" },
            {
                Header: "Status",
                accessor: "status",
                width: "14%",
                Cell: ({ value }) => <StatusBadge status={value} />,
            },
            {
                Header: "Actions",
                accessor: "actions",
                width: "12%",
                align: "center",
                disableSortBy: true,
                Cell: ({ row }) => (
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                        <IconButton size="small" onClick={() => { setSelectedTemplate(row.original); setIsModalOpen(true); }}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteClick(row.original.id)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ),
            },
        ],
        rows: templates,
    }), [templates]);


    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                                <MDBox
                                    mx={2}
                                    mt={-3}
                                    py={2}
                                    px={2}
                                    variant="gradient"
                                    bgColor="info"
                                    borderRadius="lg"
                                    coloredShadow="info"
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <MDTypography variant="h6" color="white">High Season Surcharge</MDTypography>
                                    <MDBox display="flex" alignItems="center" gap={2}>
                                        {isCreating && (
                                            <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <PearlLogoLoader size="xs" showMessage={false} message="" />
                                                <MDTypography variant="caption" color="white">
                                                    {isEditing ? 'Updating' : 'Creating'}...
                                                </MDTypography>
                                            </MDBox>
                                        )}
                                        <MDButton variant="gradient" color="dark" onClick={() => setIsModalOpen(true)}>
                                            <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                            &nbsp;Create HSS Template
                                        </MDButton>
                                    </MDBox>
                                </MDBox>
                                <MDBox pt={3}>
                                    <DataTable
                                        table={tableData}
                                        isSorted
                                        noEndBorder
                                        canSearch
                                        exportCSV
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        showTotalEntries
                                        pagination={{ variant: "gradient", color: "info" }}
                                        objectType="hss"
                                        onFilterClear={fetchTemplates}
                                    />
                                </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            <HighSeasonSurchargeModal
                open={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveTemplate}
                template={selectedTemplate}
                isSaving={isCreating}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <ConfirmDeleteModal
                open={deleteConfirm.open}
                onClose={handleCloseDeleteModal}
                onConfirm={handleDelete}
                title="Delete HSS Template"
                message="Are you sure you want to delete this HSS template? This action cannot be undone."
            />
        </DashboardLayout>
    );
}

export default HighSeasonSurcharge;